import { Event } from "../Events";
import { GlobalContext } from "../GlobalContext";
import { CancelError, CancellationToken } from "../Promises/CancellationTokenSource";
import { HeadersBuilder } from "./HeadersBuilder";
import { HTTPStatus } from "./HTTPStatus";

export type HTTPHeaders = { [name: string]: string | boolean } | Headers | HeadersBuilder;

export interface IRequestParams {
    headers?: HTTPHeaders;
    readonly responseType?: XMLHttpRequestResponseType;
    readonly okStatusSet?: HTTPStatus[];
}

export interface IRequestParamsAndData extends IRequestParams {
    readonly data?: any;
}

export interface IGetRequest extends IRequestParams {
    readonly url: string;
}

export interface IUniversalRequest extends IGetRequest, IRequestParamsAndData {
    requestMethod: string;
}

export interface IExtendedRequest extends IUniversalRequest {
    readonly readResponseInfos?: boolean;
}

const standardAllowedHTTPMethods: Set<string> = new Set(["GET", "POST", "HEAD"]);
// @ts-ignore - Condition will always return true - netroufám si kontrolu odebrat, robustnější řešení ničemu nevadí i kdyby bylo opravdu zbytečné
const supportAbortableFetch: boolean = self.fetch && (self as any).AbortController;
const supportIterableHeaders: boolean = (self as any).Headers && ((new Headers((new Map([["test", "abc"]])) as any)).get("test") === "abc"); // detekce jestli prohlížeč akceptuje jako parametr pro konstruktor Headers typ Iterable<[string,string]>, problém zaznamenán na Safari 11-
/**
* Třída pro obecné HTTP dotazy
* @includeToDoc
*/
export class HTTPRequest {
    public static useHTTPMethodOverrideHeader: boolean = true;

    private static _requestSent: Event<IExtendedRequest, IHTTPResult> = new Event<IExtendedRequest, IHTTPResult>();

    /**
     * Událost vyvolávaná po odeslání dotazu
     */
    public static get requestSent(): Event<IExtendedRequest, IHTTPResult> {
        const httpRequestClass = GlobalContext.getType<typeof HTTPRequest>(HTTPRequest);

        if (httpRequestClass._requestSent === undefined)
            httpRequestClass._requestSent = new Event<IExtendedRequest, IHTTPResult>();

        return httpRequestClass._requestSent;
    }

    /**
     * Obecná metoda pro posílání HTTP dotazů, která vrací celou odpověď včetně kódu a hlaviček
     * @param request Nastavení pro dotaz
     */
    public static async universalRequest(request: IExtendedRequest, cancellationToken: CancellationToken): Promise<IHTTPResult> {
        cancellationToken = cancellationToken || CancellationToken.none; // pro volání mimo TS

        const requstMethod = request.requestMethod;
        const overrideHttpMethod = HTTPRequest.useHTTPMethodOverrideHeader && !standardAllowedHTTPMethods.has(requstMethod);
        if (overrideHttpMethod) {
            request.requestMethod = "POST";
            request.headers = new HeadersBuilder(request.headers, { "X-HTTP-Method-Override": requstMethod });
        }

        let result: IHTTPResult;
        if (supportAbortableFetch)
            result = await HTTPRequest._universalRequestFetch(request, cancellationToken);
        else
            result = await HTTPRequest._universalRequestXHR(request, cancellationToken);

        self.setTimeout(() => HTTPRequest.requestSent.trigger(request, result)); // setTimeout kvůli ES5 v IE pro LW

        return result;
    }

    private static async _universalRequestFetch(request: IExtendedRequest, cancellationToken: CancellationToken): Promise<IHTTPResult> {
        cancellationToken.throwIfCancellationRequested();

        const controller = new (self as any).AbortController();
        const oncancel = () => controller.abort();
        const reg = cancellationToken.register(oncancel);

        const responseType = request.responseType || "text";

        try {
            const response = await fetch(request.url, {
                method: request.requestMethod,
                headers: !supportIterableHeaders && (request.headers instanceof HeadersBuilder) ? Array.from(request.headers as any) : request.headers as any, //TODO: pokud bude supportIterableHeaders ve všech námi podporovaných prohlížečích true - odstranit
                body: request.data === null ? undefined : request.data, // Edge má problém s NULL a nebere ji jako prázdnou hodnotu
                signal: controller.signal,
                credentials: "same-origin", /* pro odesílání cookies */
            } as any);
            const okStatus = request.okStatusSet ? request.okStatusSet : RequestDataProcessor.okStatuses;

            if (!okStatus.includes(response.status))
                throw new HTTPError(response, await HTTPRequest.getResponse(response, ""), request);

            return new HTTPResult(response, await HTTPRequest.getResponse(response, responseType), !!request.readResponseInfos);
        } catch (e) {
            return HTTPRequest._convertAbortError(e);
        } finally {
            reg.unregister();
        }
    }

    private static _convertAbortError(e: any): any {
        if (e.name === "AbortError") // v FF je to DOMException
            throw new CancelError("The operation was aborted.");

        /* Test
        let cts = new TescoSW.CancellationTokenSource();
        TescoSW.HTTPRequest.universalRequest({ requestMethod: "GET", url: "http://localhost/mw-dev/api/v02/as/data/FOV_hlav?select=Kod,Nazev,Stav_WF.Stav_dokladu,Datum_vystaveni,Datum_Odeslani,Termin_dodani,Datum_terminu_dodani,Zpusob_dodani,Dodavatel.Nazev,Dodavatel.Email,Splatnost_dni,Datum_splneni,Znacka,Odpovedny_prac.Nazev,Druh_objednavky.Nazev,Utvar_objednatele.Kod,Utvar_objednatele.Nazev,Zadavat_ceny_s_DPH,Suma_bez_DPH,Suma_s_DPH,Domaci_mena.Kod,Datum_synchronizace,Nakladove_stredisko.Kod,Nakladove_stredisko.Nazev,Platny,Adr_dodaci_nazev,Pocet_polozek,Schvalovatel.Kod,Schvalovatel.Nazev,Schvalovatel2.Nazev,Schvalovatel2.Kod,Datum_tisku,Last_modif_uzivatel"}, cts.token).then((r)=>logger.log(r)).catch((e)=> { logger.log(e); logger.log(e.name); });
        self.setTimeout(()=>cts.cancel(), 1);
        */

        throw e;
    }

    private static _universalRequestXHR(request: IExtendedRequest, cancellationToken: CancellationToken): Promise<IHTTPResult> {
        return new Promise((resolve, reject) => {
            cancellationToken.throwIfCancellationRequested();

            const responseType = request.responseType || "text";

            const req = new XMLHttpRequest();

            const oncancel = () => {
                req.abort();
                reject(new CancelError(undefined, cancellationToken.id));
            };

            const reg = cancellationToken.register(oncancel);

            req.onload = function(this: XMLHttpRequest) {
                try {
                    reg.unregister();

                    const okStatus = request.okStatusSet ? request.okStatusSet : RequestDataProcessor.okStatuses;
                    if (okStatus.includes(this.status)) {
                        resolve(new HTTPResult(this, this.response, !!request.readResponseInfos));
                    } else {
                        reject(new HTTPError(this, this.response, request));
                    }
                } catch (e) {
                    reject(e);
                }
            };
            req.onerror = function(this: XMLHttpRequest) {
                try {
                    reg.unregister();

                    reject(new HTTPError(this, this.response, request));
                } catch (e) {
                    reject(e);
                }
            };

            const url = request.url;

            req.open(request.requestMethod, url);

            const headers = request.headers;
            if (headers)
                for (const headerPair of (Reflect.has(headers, "entries") ? (headers as any).entries() : Object.entries(headers)))
                    req.setRequestHeader(headerPair[0], headerPair[1]);

            req.responseType = responseType;

            req.send(request.data);
        });
    }

    /**
        * Obecná metoda pro posílání HTTP dotazů
        * @param requestMethod Typ requestu ("GET", "POST", ...)
        * @param url Adresa dotazu
        * @param data Odesílaná data v těle
        * @param headers Extra hlavičky dotazu
        * @param responseType Jakého typu mají být návratová data ("text", "arraybuffer", ...)
        */
    public static request(requestMethod: string, url: string, data?: any, headers?: HTTPHeaders, responseType: XMLHttpRequestResponseType = "text", okStatusSet?: HTTPStatus[], cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        return HTTPRequest.universalRequest({
            requestMethod: requestMethod,
            url: url,
            data: data,
            headers: headers,
            responseType: responseType,
            okStatusSet: okStatusSet,
        }, cancellationToken)
            .then((responseData) => responseData.response);
    }

    /**
        * Obecná metoda pro HTTP GET dotaz
        * @param url Adresa dotazu
        * @param responseType Jakého typu mají být návratová data ("text", "arraybuffer", ...)
        * @param headers Extra hlavičky dotazu
        */
    public static get(url: string, responseType: XMLHttpRequestResponseType = "text", headers?: HTTPHeaders, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        return HTTPRequest.universalRequest({
            requestMethod: "GET",
            url: url,
            headers: headers,
            responseType: responseType,
        }, cancellationToken)
            .then((responseData) => responseData.response);
    }

    /**
        * Obecná metoda pro HTTP GET dotaz, která vrací celou odpověď včetně hlaviček
        * @param url Adresa dotazu
        * @param responseType Jakého typu mají být návratová data ("text", "arraybuffer", ...)
        * @param headers Extra hlavičky dotazu
        * @param okStatusSet Množina přijatelných stavových HTTP kódů
        */
    public static getWithWholeReponse(url: string, responseType: XMLHttpRequestResponseType = "text", headers?: HTTPHeaders, okStatusSet?: HTTPStatus[], cancellationToken: CancellationToken = CancellationToken.none): Promise<IHTTPResult> {
        return HTTPRequest.universalRequest({
            requestMethod: "GET",
            url: url,
            headers: headers,
            responseType: responseType,
            okStatusSet: okStatusSet,
            readResponseInfos: true,
        }, cancellationToken);
    }

    public static readonly jsonHeaders = {
        "Content-Type": "application/json; charset=utf-8",
        "Accept": "application/json, text/javascript, */*; q=0.01",
    };

    /**
        * Obecná metoda pro JSON dotaz
        * @param requestMethod Typ requestu ("GET", "POST", ...)
        * @param url Adresa dotazu
        * @param additionalData Další data, které můžou víc specifikovat dotaz
        */
    public static async jsonRequest(requestMethod: string, url: string, cancellationToken: CancellationToken, additionalData: IRequestParamsAndData): Promise<any> {
        const headers = !additionalData.headers ? HTTPRequest.jsonHeaders : new HeadersBuilder(HTTPRequest.jsonHeaders, additionalData.headers);
        const responseData = await HTTPRequest.universalRequest({ requestMethod: requestMethod, url: url, data: JSON.stringify(additionalData.data), headers: headers, okStatusSet: additionalData.okStatusSet }, cancellationToken);
        return !responseData.response ? null : JSON.parse(responseData.response); // ideal responseData.json();
    }

    public static getHeader(response: XMLHttpRequest | Response, headerName: string): string | null {
        if (response instanceof XMLHttpRequest)
            return response.getResponseHeader(headerName);
        else
            return response.headers.get(headerName);
    }

    public static async getResponse(responseData: XMLHttpRequest | Response, responseType: XMLHttpRequestResponseType): Promise<any> {
        if (responseData instanceof Response)
            try {
                switch (responseType) {
                    case "arraybuffer":
                        return await responseData.arrayBuffer();
                    case "blob":
                        return await responseData.blob();
                    case "json":
                        return await responseData.json();
                    default:
                        return await responseData.text();
                }
            } catch (e) {
                HTTPRequest._convertAbortError(e);
            }
        else {
            const covertError = () => {
                throw new TypeError(`Can not convert response from "${responseData.responseType}" to "${responseType}"`);
            };

            if (!responseType)
                return responseData.response;

            if (!responseData.responseType || responseData.responseType == "text")
                switch (responseType) {
                    case "text":
                        return responseData.response;
                    case "json":
                        return !responseData.response ? null : JSON.parse(responseData.response);
                    default:
                        covertError();
                }
            else if (responseData.responseType != responseType)
                covertError();

            return responseData.response;
        }
    }
}

GlobalContext.registerType(HTTPRequest);

class RequestDataProcessor {
    public static readonly okStatuses = [HTTPStatus.ok];

    public static setResponseInfos(httpResponse: IHTTPResult, serverResponse: XMLHttpRequest | Response, readResponseInfos: boolean): void {
        if (readResponseInfos) {
            httpResponse.status = serverResponse.status;
            httpResponse.statusText = serverResponse.statusText;
            const conType = serverResponse instanceof XMLHttpRequest ? serverResponse.getResponseHeader("Content-Type") : serverResponse.headers.get("Content-Type");
            httpResponse.contentType = conType ? conType : undefined;
        }
    }
}

export interface IHTTPResult {
    status?: HTTPStatus;
    statusText?: string;
    response: any;

    contentType?: string;
    /**
        * Vrátí hodnotu požadované HTTP hlavičky nebo null, pokud nebyla nalezena. Vyhledává case-insensitive.
        * Hlavičky jsou ze standardu c-i, takže je vždy preferovaná tato metoda pro přístup k nim.
        * Chrome od vyšších verzí (cca 50+) si dokonce všechny přijaté hlavičky převádí na lower-case.
        * @param header Název hledané hlavičky
        */
    getResponseHeader: (header: string) => string | null;
}

// TODO: chtělo by to předělat na asynchronní dočítání response. Ideálně async metody json, text, ... jak jsou na fetch

/**
    * Výjimka vyvolávaná při HTTP chybách
    */
export class HTTPError extends Error implements IHTTPResult {
    public readonly status: HTTPStatus | undefined;
    public readonly statusText: string | undefined;
    public readonly contentType?: string | undefined;
    public readonly response: any | undefined;
    public readonly request: IExtendedRequest;

    private readonly _serverResponse: XMLHttpRequest | Response | undefined;

    constructor(serverResponse: XMLHttpRequest | Response, responseBody: any, request: IExtendedRequest) {
        super(`${serverResponse.status} - ${serverResponse.statusText}`);
        this.response = responseBody;
        this.request = request;
        RequestDataProcessor.setResponseInfos(this, serverResponse, true);
    }

    public getResponseHeader(header: string): string | null {
        return HTTPRequest.getHeader(this._serverResponse!, header);
    }
}

class HTTPResult implements IHTTPResult {
    public readonly status: HTTPStatus | undefined;
    public readonly statusText: string | undefined;
    public readonly contentType?: string;
    public readonly response: any;

    /*
    TODO: časem namísto response
    public json(): Promise<any> { return HTTPRequest.getResponse(this._serverResponse, "json"); }
    public text(): Promise<any> { return HTTPRequest.getResponse(this._serverResponse, "text"); }
    public getResponse(responseType: XMLHttpRequestResponseType): Promise<any> { return HTTPRequest.getResponse(this._serverResponse, responseType); }
    */

    private readonly _serverResponse: XMLHttpRequest | Response;

    constructor(serverResponse: XMLHttpRequest | Response, responseBody: any, readResponseInfos: boolean) {
        this._serverResponse = serverResponse;
        this.response = responseBody;
        RequestDataProcessor.setResponseInfos(this, serverResponse, readResponseInfos);
    }

    public getResponseHeader(header: string): string | null {
        return HTTPRequest.getHeader(this._serverResponse, header);
    }
}
